import Styles from './Header.module.scss'
import { ArrowLeft, Calendar1, Calendar2, Edit1 } from 'blaze-ui/icons'
import { generatePath, useRouteMatch, useHistory, Link } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH, SCHEDULED_POSTS_PATH } from 'paths'
import { Button } from 'blaze-ui/Button'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { Text } from 'blaze-ui/Text/Text'
import {
  STATUS_COLOR,
  STATUS_COLOR_V2,
  STATUS_FONT_COLOR_V2,
} from 'screens/ScheduledPosts/MobileCalendar/components/CalendarItem/CalendarItem'
import { Tag } from 'common/OptionSelect/Tag'
import { forwardRef, useEffect, useState } from 'react'
import cx from 'classnames'
import { useQueryClient } from 'react-query'
import { removeItemFromCalendarCache } from 'hooks/queries/calendar/utils'
import { HeaderProps } from './Types'
import { PostActionsButton } from './PostActionsButton'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { SOCIAL_POST_PLATFORMS } from 'components/ScheduledPostTargetsPreview/ScheduledPostTargetsPreview'
import { ScheduledPostTarget } from 'types'
import { useCalendarTargetAnalysis } from 'hooks/useCalendarTargetAnalysis'
import { AlertTriangle } from 'blaze-ui/icons'
import { getUniqueTargetsByProvider } from 'utils/scheduledPostTargetUtils'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { AlmanacFileCalendarEvent } from '../types'
import { optimisticRemoveFilesFromInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useIsVirtualMarketer } from 'hooks/useIsVirtualMarketer'
import { usePreviewTitleAlignment } from './usePreviewTitleAlignment'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { ProfileDropdown } from 'blaze-components/TopBar/ProfileDropdown/ProfileDropdown'
import { VirtualMarketerUpgradeButton } from 'blaze-components/VirtualMarketerUpgradeButton/VirtualMarketerUpgradeButton'
import { AutopilotProUpgradeButton } from 'components/AutopilotUpgradeButtons/AutopilotProUpgradeButton'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { CreditUsageDropdown } from 'blaze-components/CreditUsageDropdown/CreditUsageDropdown'

const isPublishingStatus = (status: ScheduledPostTarget['status']): boolean =>
  status === 'queued' || status === 'processing' || status === 'retrying'

export const isAnyTargetProcessing = (targets: ScheduledPostTarget[]): boolean =>
  targets.some((target) => isPublishingStatus(target.status))

export const Header = forwardRef<HTMLDivElement, HeaderProps>(
  (
    { id, startDate, almanacFile, isIntersecting, onClose, isLoading, setIsLoading, onDelete, filters, channel },
    ref,
  ) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const isVirtualMarketer = useIsVirtualMarketer()
    const workspaceId = useCurrentWorkspaceId()
    const currentWorkspace = useCurrentWorkspace()
    const workspaces = useWorkspaces()
    const queryClient = useQueryClient()
    const isOnScheduledPosts = useRouteMatch(SCHEDULED_POSTS_PATH)
    const history = useHistory()
    const [refetchCalendarItems, setRefetchCalendarItems] = useState(false)
    const isArticlePreview = almanacFile?.asset_type === 'Article'

    const {
      leftBoundaryRef: leftCornerRef,
      rightBoundaryRef: rightCornerRef,
      contentRef: titleContainerRef,
      position: titlePosition,
    } = usePreviewTitleAlignment({ isArticlePreview, postId: id })

    const { connectedBlogIntegration } = useBlogIntegrations()
    const wixEnabled = useFlipperFlag('wix_blog_integration')
    const shopifyEnabled = useFlipperFlag('shopify_integration')
    const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
    const { data: brandPlan } = useCurrentBrandPlan()

    const fileChannel = almanacFile?.channel || channel

    const to = isArticlePreview ? generatePath(DOC_PATH, { slug: id }) : generatePath(DESIGN_PATH, { key: id })

    const blogPlatformBranding = fileChannel
      ? getBlogPlatformBrandingWithOnboardingSelection({
          channel: fileChannel,
          externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
          selectedBlogPlatform: brandPlan?.selected_blog_platform,
          featureFlags: {
            wixEnabled,
            blogPlatformOnboardingBrandingEnabled,
            shopifyEnabled,
          },
        })
      : null

    const sortedScheduledPostTargets =
      almanacFile?.scheduled_post_targets?.sort((a, b) => {
        const aIndex = SOCIAL_POST_PLATFORMS.indexOf(a.provider as any)
        const bIndex = SOCIAL_POST_PLATFORMS.indexOf(b.provider as any)
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        }
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1
        return 0
      }) || []

    const targetAnalysis = useCalendarTargetAnalysis({
      ...almanacFile,
      scheduled_post_targets: almanacFile?.scheduled_post_targets || [],
    } as AlmanacFileCalendarEvent)

    const isHybrid = !targetAnalysis.useOldLogic && targetAnalysis.isHybrid

    useEffect(
      () => () => {
        if (refetchCalendarItems) {
          queryClient.refetchQueries(calendarKeys.detailAll())
          setRefetchCalendarItems(false)
        }
      },
      [queryClient, refetchCalendarItems],
    )

    const handleCalendarButtonPress = () => {
      if (isOnScheduledPosts) {
        onClose()
      } else {
        onClose()
        const scheduledPostsPath = generatePath(SCHEDULED_POSTS_PATH, { workspaceId })
        history.push(scheduledPostsPath)
      }
    }

    const handleDelete = (almanacFileKey?: string) => {
      if (!almanacFileKey) return

      onDelete()
      removeItemFromCalendarCache(queryClient, almanacFileKey)
      queryClient.invalidateQueries(calendarKeys.detailAll())
      queryClient.invalidateQueries(calendarKeys.all)
      queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))
      optimisticRemoveFilesFromInfiniteLists(queryClient, workspaceId, [almanacFileKey])
    }

    const didGenerationFail = almanacFile?.did_generation_fail
    // Handle both AlmanacFileShowItem (post_status) and AlmanacFileCalendarItem (status)
    let tagStatus: string | PostStatusPropertyValue | null | undefined
    if (didGenerationFail) {
      tagStatus = 'Failed'
    } else if (almanacFile && 'post_status' in almanacFile) {
      tagStatus = almanacFile.post_status
    } else {
      tagStatus = (almanacFile as any)?.status
    }

    return (
      <div className={cx(Styles.header, { [Styles.isIntersecting]: isIntersecting })} ref={ref}>
        <div className={cx(Styles.headerContent, Styles.forCrossPost)}>
          <div className={Styles.leftCorner} ref={leftCornerRef}>
            {isSystemRedesignEnabled ? (
              <>
                <IconButton aria-label="Back" onPress={onClose} icon={ArrowLeft} size="sm" variant="secondary" />
                <IconButton
                  aria-label="Calendar"
                  onPress={handleCalendarButtonPress}
                  icon={Calendar1}
                  size="sm"
                  variant="secondary"
                />
              </>
            ) : (
              <Button variant="tertiary" frontIcon={ArrowLeft} rounded size="lg" onPress={onClose}>
                Back
              </Button>
            )}
          </div>
          <div className={Styles.center}>
            <div
              ref={titleContainerRef}
              className={Styles.titleContainer}
              style={titlePosition ? { left: titlePosition.left, maxWidth: titlePosition.maxWidth } : undefined}
            >
              {sortedScheduledPostTargets.length > 1 ? (
                <div className={Styles.platformIconsContainer}>
                  {getUniqueTargetsByProvider(sortedScheduledPostTargets).map((target, index, uniqueTargets) => (
                    <div
                      key={target.id}
                      className={cx(Styles.platformIcon, Styles.fullScreenPreview)}
                      style={{ zIndex: uniqueTargets.length - index }}
                    >
                      <PlatformIcon platform={target.provider} size={24} />
                    </div>
                  ))}
                </div>
              ) : (
                <ChannelIcon
                  channel={fileChannel}
                  size={24}
                  isTransparentBackground={false}
                  blogPlatform={blogPlatformBranding}
                />
              )}
              <Text variant={isSystemRedesignEnabled ? 'secondary' : 'smallList'} color="var(--dark-80)" lineClamp={1}>
                {almanacFile?.title}
              </Text>
              {isAnyTargetProcessing(almanacFile?.scheduled_post_targets || []) ? (
                <Tag size="md" color="#ffefc5" border={isSystemRedesignEnabled ? '1px solid var(--dark-4)' : undefined}>
                  Posting...
                </Tag>
              ) : (
                tagStatus &&
                Object.keys(STATUS_COLOR).includes(tagStatus) && (
                  <div className={Styles.statusContainer}>
                    {isSystemRedesignEnabled ? (
                      <Tag
                        size="md"
                        color={STATUS_COLOR_V2[tagStatus]}
                        fontColor={STATUS_FONT_COLOR_V2[tagStatus]}
                        border={'1px solid var(--dark-4)'}
                      >
                        {tagStatus}
                      </Tag>
                    ) : (
                      <Tag size="md" color={STATUS_COLOR[tagStatus]}>
                        {tagStatus}
                      </Tag>
                    )}
                    {isHybrid && !targetAnalysis.useOldLogic && (
                      <Tooltip
                        label={`${targetAnalysis.successfulTargets.length} successful, ${targetAnalysis.failedTargets.length} failed`}
                        placement="top"
                      >
                        <div className={Styles.hybridAlertIcon} data-testid="hybrid-warning-icon">
                          <AlertTriangle size={16} color="var(--negative-60)" />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                )
              )}
              {isSystemRedesignEnabled && (
                <PostActionsButton
                  almanacFile={almanacFile}
                  isDisabled={isLoading}
                  onDelete={() => handleDelete(almanacFile?.key)}
                />
              )}
            </div>
          </div>
          <div className={Styles.rightCorner} ref={rightCornerRef}>
            {!isVirtualMarketer && (
              <ButtonLink
                frontIcon={Edit1}
                size="lg"
                rounded
                variant="tertiary"
                isDisabled={isLoading}
                elementType={Link}
                to={to}
                data-testid={`edit-in-${almanacFile?.asset_type === 'Design' ? 'Designer' : 'Writer'}-button`}
                onPress={onClose}
              >
                Edit in {almanacFile?.asset_type === 'Design' ? 'Designer' : 'Writer'}
              </ButtonLink>
            )}
            {!isSystemRedesignEnabled && (
              <Button variant="tertiary" frontIcon={Calendar2} rounded size="lg" onPress={handleCalendarButtonPress}>
                Calendar
              </Button>
            )}
            {isSystemRedesignEnabled ? (
              <>
                {currentWorkspace?.is_ap_freemium ? (
                  <AutopilotProUpgradeButton variant="upgrade" />
                ) : (
                  <VirtualMarketerUpgradeButton showTrialDaysLeft={false} />
                )}
                <CreditUsageDropdown />
                <ProfileDropdown selectedWorkspaceId={workspaceId} workspaces={workspaces} />
              </>
            ) : (
              <PostActionsButton
                almanacFile={almanacFile}
                isDisabled={isLoading}
                onDelete={() => handleDelete(almanacFile?.key)}
              />
            )}
          </div>
        </div>
      </div>
    )
  },
)
